import { isEditMode } from '../core/edit-mode.js';

export interface CommerceManifest {
  schema_version?: number;
  page_id: number;
  source_hash: string;
  signature?: string;
  resources?: {
    offers?: Array<{ public_id: string }>;
    variants?: Array<{ public_id: string }>;
  };
}

interface CommerceProjection {
  public_id: string;
  title: string;
  price: string;
  currency: string;
  available: boolean;
  version: string;
  product: { title: string; slug: string | null };
  discount?: string | null;
  prepayment?: { amount: string; description: string | null; deadline: string | null } | null;
  installment?: {
    price: string;
    count: number;
    frequency_days: number | null;
    starts_at: string | null;
  } | null;
  sku?: string | null;
}

export interface PageCommerceData {
  source_hash: string;
  offers: Record<string, CommerceProjection>;
  variants: Record<string, CommerceProjection>;
}

const MANIFEST_ELEMENT_ID = 'plp-commerce-manifest';
export const COMMERCE_CART_STORAGE_KEY = 'plp-commerce-cart-v1';
export const COMMERCE_ORDERS_STORAGE_KEY = 'plp-commerce-orders-v1';
const ACTION_SELECTOR = [
  '[data-plp-action="checkout-offer"]',
  '[data-plp-action="checkout-variant"]',
  '[data-plp-action="cart-add"]',
  '[data-plp-action="cart-open"]',
].join(',');
const BINDING_SELECTOR = '[data-plp-bind-kind][data-plp-bind-public-id][data-plp-bind-field]';
const CART_COUNT_SELECTOR = '[data-plp-cart-count]';
const RESUME_CONFIG_SELECTOR = '[data-plp-commerce-resume]';
const RESUME_DISMISSED_SESSION_KEY = 'plp-commerce-resume-dismissed-v1';
const REMEMBERED_ORDER_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;

export interface CommerceCartItem {
  variant_public_id: string;
  qty: number;
}

export interface CommerceCartSnapshot {
  schema_version: 1;
  page_id: number;
  source_hash: string;
  manifest: CommerceManifest;
  items: CommerceCartItem[];
  updated_at: string;
}

export interface RememberedCommerceOrder {
  hash: string;
  path: string;
  amount: string;
  currency: string;
  kind: 'offer' | 'cart';
  page_id?: number;
  source_public_id?: string;
  saved_at: string;
}

export interface ResolvedCommerceOrder {
  hash: string;
  title: string;
  amount: string;
  currency: string;
  status: 'new' | 'partly_paid';
  expires_at: string | null;
  path: string;
  source_public_id?: string;
}

export interface PendingOrderChoice {
  decision: 'continue' | 'new' | 'cancel';
  order?: ResolvedCommerceOrder;
}

interface ResumeWidgetConfig {
  theme: 'auto' | 'light' | 'dark';
  position: 'bottom-left' | 'bottom-right';
  detail: 'compact' | 'summary';
  delay: number;
  showCart: boolean;
  showPendingOrder: boolean;
}

const ORDER_HASH_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

let pageDataPromise: Promise<PageCommerceData> | null = null;
let resolvedOrdersPromise: Promise<ResolvedCommerceOrder[]> | null = null;
let clickListenerInstalled = false;
let resumeCartListenerInstalled = false;

export function readCommerceManifest(): CommerceManifest | null {
  const element = document.getElementById(MANIFEST_ELEMENT_ID);
  if (!element?.textContent) return null;
  try {
    const value = JSON.parse(element.textContent) as Partial<CommerceManifest>;
    if (!Number.isSafeInteger(value.page_id) || Number(value.page_id) <= 0) return null;
    if (typeof value.source_hash !== 'string' || !value.source_hash) return null;
    return value as CommerceManifest;
  } catch {
    return null;
  }
}

export function loadPageCommerceData(): Promise<PageCommerceData> {
  if (pageDataPromise) return pageDataPromise;
  const manifest = readCommerceManifest();
  if (!manifest) return Promise.reject(new Error('commerce manifest is missing'));

  pageDataPromise = fetch(`/api/checkout/page/${manifest.page_id}/data`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ manifest }),
  })
    .then(async (response) => {
      const body = (await response.json().catch(() => null)) as PageCommerceData | null;
      if (!response.ok || !body || body.source_hash !== manifest.source_hash) {
        throw new Error(`commerce page data returned ${response.status}`);
      }
      return body;
    })
    .catch((error) => {
      pageDataPromise = null;
      throw error;
    });

  return pageDataPromise;
}

function formatMoney(value: string, currency: string): string {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return value;
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'ru', {
      style: 'currency',
      currency,
      maximumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    }).format(amount);
  } catch {
    return `${value} ${currency}`;
  }
}

function bindingValue(projection: CommerceProjection, field: string): string | null {
  switch (field) {
    case 'title':
      return projection.title;
    case 'product_title':
      return projection.product.title;
    case 'sku':
      return projection.sku ?? null;
    case 'price':
      return projection.price;
    case 'formatted_price':
      return formatMoney(projection.price, projection.currency);
    case 'discount':
      return projection.discount ?? null;
    case 'prepayment':
      return projection.prepayment
        ? formatMoney(projection.prepayment.amount, projection.currency)
        : null;
    case 'installment_payment':
      return projection.installment
        ? formatMoney(
            (Number(projection.installment.price) / projection.installment.count).toFixed(2),
            projection.currency
          )
        : null;
    case 'availability':
      return projection.available ? 'available' : 'unavailable';
    default:
      return null;
  }
}

export function hydrateCommerceBindings(root: ParentNode, data: PageCommerceData): void {
  const bindings = Array.from(root.querySelectorAll<HTMLElement>(BINDING_SELECTOR));
  if (root instanceof HTMLElement && root.matches(BINDING_SELECTOR)) bindings.unshift(root);
  for (const element of bindings) {
    const kind = element.dataset.plpBindKind;
    const publicId = element.dataset.plpBindPublicId;
    const field = element.dataset.plpBindField;
    if (!publicId || !field) continue;
    const projection = kind === 'variant' ? data.variants[publicId] : data.offers[publicId];
    if (!projection) continue;
    const value = bindingValue(projection, field);
    if (value !== null) element.textContent = value;
    element.dataset.plpCommerceAvailable = String(projection.available);
  }

  const actions = Array.from(root.querySelectorAll<HTMLElement>(ACTION_SELECTOR));
  if (root instanceof HTMLElement && root.matches(ACTION_SELECTOR)) actions.unshift(root);
  for (const element of actions) {
    const action = element.dataset.plpAction;
    if (action === 'cart-open') continue;
    const publicId =
      action === 'checkout-offer'
        ? element.dataset.plpOfferPublicId
        : element.dataset.plpVariantPublicId;
    const projection = publicId
      ? action === 'checkout-offer'
        ? data.offers[publicId]
        : data.variants[publicId]
      : null;
    const available = projection?.available === true;
    element.dataset.plpCommerceAvailable = String(available);
    element.setAttribute('aria-disabled', String(!available));
    if (element instanceof HTMLButtonElement) element.disabled = !available;
  }
}

function storageRead(): CommerceCartSnapshot | null {
  try {
    const parsed = JSON.parse(
      localStorage.getItem(COMMERCE_CART_STORAGE_KEY) ?? 'null'
    ) as Partial<CommerceCartSnapshot> | null;
    if (!parsed || parsed.schema_version !== 1 || !Array.isArray(parsed.items)) return null;
    if (!Number.isSafeInteger(parsed.page_id) || typeof parsed.source_hash !== 'string')
      return null;
    if (!parsed.manifest || parsed.manifest.page_id !== parsed.page_id) return null;
    const items = parsed.items.filter(
      (item): item is CommerceCartItem =>
        typeof item?.variant_public_id === 'string' &&
        Number.isSafeInteger(item.qty) &&
        item.qty > 0 &&
        item.qty <= 999
    );
    return { ...parsed, items } as CommerceCartSnapshot;
  } catch {
    return null;
  }
}

export function readCommerceCart(): CommerceCartSnapshot | null {
  const manifest = readCommerceManifest();
  const cart = storageRead();
  if (!manifest || !cart) return null;
  if (cart.page_id !== manifest.page_id || cart.source_hash !== manifest.source_hash) return null;
  return cart;
}

export function rememberCommerceOrder(
  order: Pick<RememberedCommerceOrder, 'hash' | 'path' | 'amount' | 'currency'>,
  kind: RememberedCommerceOrder['kind'],
  pageId = readCommerceManifest()?.page_id,
  sourcePublicId?: string
): void {
  if (!ORDER_HASH_RE.test(order.hash)) return;
  if (order.path !== `/checkout/order/${order.hash}`) return;
  try {
    const stored = JSON.parse(localStorage.getItem(COMMERCE_ORDERS_STORAGE_KEY) ?? '[]');
    const previous = Array.isArray(stored) ? stored : [];
    const next: RememberedCommerceOrder[] = [
      {
        hash: order.hash,
        path: order.path,
        amount: order.amount,
        currency: order.currency,
        kind,
        ...(pageId ? { page_id: pageId } : {}),
        ...(sourcePublicId ? { source_public_id: sourcePublicId } : {}),
        saved_at: new Date().toISOString(),
      },
      ...previous.filter((item) => item?.hash !== order.hash),
    ].slice(0, 20);
    localStorage.setItem(COMMERCE_ORDERS_STORAGE_KEY, JSON.stringify(next));
    resolvedOrdersPromise = null;
  } catch {
    // Privacy mode / quota: заказ уже создан, local resume не критичен.
  }
}

export async function promptPendingOrderForOffer(publicId: string): Promise<PendingOrderChoice> {
  let orders: ResolvedCommerceOrder[];
  try {
    orders = await resolveRememberedCommerceOrders();
  } catch {
    return { decision: 'new' };
  }
  const order = orders.find((item) => item.source_public_id === publicId);
  if (!order) return { decision: 'new' };
  if (typeof HTMLDialogElement === 'undefined') return { decision: 'new' };

  return new Promise((resolve) => {
    const dialog = document.createElement('dialog');
    dialog.className = 'plp-commerce-pending-dialog';
    dialog.setAttribute('aria-labelledby', 'plp-commerce-pending-title');

    const title = document.createElement('h2');
    title.id = 'plp-commerce-pending-title';
    title.className = 'plp-commerce-pending-dialog__title';
    title.textContent = 'У вас уже есть неоплаченный заказ';
    const text = document.createElement('p');
    text.className = 'plp-commerce-pending-dialog__text';
    text.textContent = `${order.title} · ${formatMoney(order.amount, order.currency)}`;
    const actions = document.createElement('div');
    actions.className = 'plp-commerce-pending-dialog__actions';
    const continueButton = document.createElement('button');
    continueButton.type = 'button';
    continueButton.className = 'plp-commerce-pending-dialog__primary';
    continueButton.textContent = 'Продолжить оплату';
    const newButton = document.createElement('button');
    newButton.type = 'button';
    newButton.className = 'plp-commerce-pending-dialog__secondary';
    newButton.textContent = 'Оформить новый';
    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'plp-commerce-pending-dialog__close';
    closeButton.setAttribute('aria-label', 'Закрыть');
    closeButton.textContent = '×';
    actions.append(continueButton, newButton);
    dialog.append(closeButton, title, text, actions);
    document.body.appendChild(dialog);

    let completed = false;
    const finish = (choice: PendingOrderChoice) => {
      if (completed) return;
      completed = true;
      dialog.remove();
      resolve(choice);
    };
    continueButton.addEventListener('click', () => finish({ decision: 'continue', order }));
    newButton.addEventListener('click', () => finish({ decision: 'new' }));
    closeButton.addEventListener('click', () => finish({ decision: 'cancel' }));
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      finish({ decision: 'cancel' });
    });
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else finish({ decision: 'new' });
  });
}

function writeRememberedOrders(orders: RememberedCommerceOrder[]): void {
  try {
    localStorage.setItem(COMMERCE_ORDERS_STORAGE_KEY, JSON.stringify(orders.slice(0, 20)));
  } catch {
    // Privacy mode / quota: серверный заказ остаётся источником истины.
  }
}

export function readRememberedCommerceOrders(): RememberedCommerceOrder[] {
  try {
    const stored = JSON.parse(localStorage.getItem(COMMERCE_ORDERS_STORAGE_KEY) ?? '[]');
    if (!Array.isArray(stored)) return [];
    const cutoff = Date.now() - REMEMBERED_ORDER_MAX_AGE_MS;
    const seen = new Set<string>();
    const valid = stored.filter((item): item is RememberedCommerceOrder => {
      if (!item || typeof item !== 'object' || !ORDER_HASH_RE.test(item.hash)) return false;
      if (item.path !== `/checkout/order/${item.hash}` || seen.has(item.hash)) return false;
      if (!['offer', 'cart'].includes(item.kind)) return false;
      if (typeof item.amount !== 'string' || typeof item.currency !== 'string') return false;
      if (
        item.source_public_id !== undefined &&
        (typeof item.source_public_id !== 'string' || item.source_public_id.length > 64)
      )
        return false;
      const savedAt = Date.parse(item.saved_at);
      if (!Number.isFinite(savedAt) || savedAt < cutoff) return false;
      seen.add(item.hash);
      return true;
    });
    const bounded = valid.slice(0, 20);
    if (bounded.length !== stored.length) writeRememberedOrders(bounded);
    return bounded;
  } catch {
    return [];
  }
}

function isResolvedOrder(value: unknown): value is ResolvedCommerceOrder {
  if (!value || typeof value !== 'object') return false;
  const order = value as Partial<ResolvedCommerceOrder>;
  return (
    typeof order.hash === 'string' &&
    ORDER_HASH_RE.test(order.hash) &&
    order.path === `/checkout/order/${order.hash}` &&
    typeof order.title === 'string' &&
    typeof order.amount === 'string' &&
    typeof order.currency === 'string' &&
    (order.status === 'new' || order.status === 'partly_paid') &&
    (order.expires_at === null || typeof order.expires_at === 'string') &&
    (order.source_public_id === undefined ||
      (typeof order.source_public_id === 'string' && order.source_public_id.length <= 64))
  );
}

export function resolveRememberedCommerceOrders(): Promise<ResolvedCommerceOrder[]> {
  if (resolvedOrdersPromise) return resolvedOrdersPromise;
  const remembered = readRememberedCommerceOrders();
  if (remembered.length === 0) return Promise.resolve([]);

  resolvedOrdersPromise = fetch('/api/checkout/orders/resolve-saved', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ hashes: remembered.map((order) => order.hash) }),
  })
    .then(async (response) => {
      const body = (await response.json().catch(() => null)) as { orders?: unknown[] } | null;
      if (!response.ok || !Array.isArray(body?.orders)) {
        throw new Error(`saved orders resolve returned ${response.status}`);
      }
      const orders = body.orders.filter(isResolvedOrder);
      const resolvedByHash = new Map(orders.map((order) => [order.hash, order]));
      writeRememberedOrders(
        remembered.flatMap((local) => {
          const server = resolvedByHash.get(local.hash);
          return server
            ? [{ ...local, amount: server.amount, currency: server.currency, path: server.path }]
            : [];
        })
      );
      return orders;
    })
    .catch((error) => {
      resolvedOrdersPromise = null;
      throw error;
    });
  return resolvedOrdersPromise;
}

function booleanData(value: string | undefined, fallback: boolean): boolean {
  if (value === undefined || value === '') return fallback;
  return !['false', 'off', '0', 'no'].includes(value.toLowerCase());
}

function readResumeWidgetConfig(): ResumeWidgetConfig | null {
  const marker = document.querySelector<HTMLElement>(RESUME_CONFIG_SELECTOR);
  if (!marker || !booleanData(marker.dataset.plpCommerceResume, false)) return null;
  const theme = ['light', 'dark'].includes(marker.dataset.plpResumeTheme ?? '')
    ? (marker.dataset.plpResumeTheme as 'light' | 'dark')
    : 'auto';
  const position =
    marker.dataset.plpResumePosition === 'bottom-left' ? 'bottom-left' : 'bottom-right';
  const detail = marker.dataset.plpResumeDetail === 'compact' ? 'compact' : 'summary';
  const rawDelay = Number(marker.dataset.plpResumeDelay ?? '1500');
  const delay = Number.isFinite(rawDelay) ? Math.min(10_000, Math.max(0, rawDelay)) : 1500;
  return {
    theme,
    position,
    detail,
    delay,
    showCart: booleanData(marker.dataset.plpResumeShowCart, true),
    showPendingOrder: booleanData(marker.dataset.plpResumeShowPendingOrder, true),
  };
}

function resumeCopy() {
  const russian = (document.documentElement.lang || 'ru').toLowerCase().startsWith('ru');
  return russian
    ? {
        orderTitle: 'У вас есть неоплаченный заказ',
        continue: 'Продолжить оплату',
        cart: (count: number) => `В корзине ${count} ${count === 1 ? 'товар' : 'товара'}`,
        openCart: 'Открыть корзину',
        more: (count: number) => `Ещё ${count}`,
        close: 'Скрыть напоминание',
      }
    : {
        orderTitle: 'You have an unpaid order',
        continue: 'Continue payment',
        cart: (count: number) => `${count} item${count === 1 ? '' : 's'} in cart`,
        openCart: 'Open cart',
        more: (count: number) => `${count} more`,
        close: 'Dismiss reminder',
      };
}

function resumeKey(order: ResolvedCommerceOrder | null, cart: CommerceCartSnapshot | null): string {
  return order ? `order:${order.hash}` : `cart:${cart?.updated_at ?? ''}`;
}

function renderResumeWidget(config: ResumeWidgetConfig, orders: ResolvedCommerceOrder[]): void {
  document.querySelector('.plp-commerce-resume')?.remove();
  const order = config.showPendingOrder ? (orders[0] ?? null) : null;
  const cart = config.showCart ? readCommerceCart() : null;
  const cartCount = cart?.items.reduce((sum, item) => sum + item.qty, 0) ?? 0;
  if (!order && cartCount === 0) return;
  const key = resumeKey(order, cart);
  if (sessionStorage.getItem(RESUME_DISMISSED_SESSION_KEY) === key) return;

  const copy = resumeCopy();
  const panel = document.createElement('aside');
  panel.className = `plp-commerce-resume plp-commerce-resume--${config.position}`;
  panel.dataset.theme = config.theme;
  panel.dataset.detail = config.detail;
  panel.setAttribute('role', 'status');
  panel.setAttribute('aria-live', 'polite');

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'plp-commerce-resume__close';
  close.setAttribute('aria-label', copy.close);
  close.textContent = '×';
  close.addEventListener('click', () => {
    sessionStorage.setItem(RESUME_DISMISSED_SESSION_KEY, key);
    panel.remove();
  });

  const content = document.createElement('div');
  content.className = 'plp-commerce-resume__content';
  const heading = document.createElement('strong');
  heading.className = 'plp-commerce-resume__heading';
  heading.textContent = order ? copy.orderTitle : copy.cart(cartCount);
  const summary = document.createElement('span');
  summary.className = 'plp-commerce-resume__summary';
  summary.textContent = order
    ? `${order.title} · ${formatMoney(order.amount, order.currency)}`
    : copy.cart(cartCount);
  content.append(heading);
  if (config.detail === 'summary') content.append(summary);
  if (order && orders.length > 1 && config.detail === 'summary') {
    const more = document.createElement('span');
    more.className = 'plp-commerce-resume__more';
    more.textContent = copy.more(orders.length - 1);
    content.append(more);
  }

  const action = document.createElement('button');
  action.type = 'button';
  action.className = 'plp-commerce-resume__action';
  action.textContent = order ? copy.continue : copy.openCart;
  action.addEventListener('click', () => {
    openCheckout(order?.path ?? '/checkout/cart', 'modal');
  });
  panel.append(close, content, action);
  document.body.appendChild(panel);
}

async function initResumeWidget(): Promise<void> {
  const config = readResumeWidgetConfig();
  if (!config || isEditMode()) return;
  let orders: ResolvedCommerceOrder[] = [];
  if (config.showPendingOrder) {
    try {
      orders = await resolveRememberedCommerceOrders();
    } catch {
      // При сетевой ошибке не чистим storage и не показываем недоверенную сумму.
    }
  }
  window.setTimeout(() => renderResumeWidget(config, orders), config.delay);
}

function writeCommerceCart(items: CommerceCartItem[]): CommerceCartSnapshot {
  const manifest = readCommerceManifest();
  if (!manifest) throw new Error('commerce manifest is missing');
  const snapshot: CommerceCartSnapshot = {
    schema_version: 1,
    page_id: manifest.page_id,
    source_hash: manifest.source_hash,
    manifest,
    items,
    updated_at: new Date().toISOString(),
  };
  localStorage.setItem(COMMERCE_CART_STORAGE_KEY, JSON.stringify(snapshot));
  hydrateCartIndicators(document, snapshot);
  document.dispatchEvent(
    new CustomEvent('plp:cart-updated', {
      detail: {
        items: snapshot.items,
        count: snapshot.items.reduce((sum, item) => sum + item.qty, 0),
      },
    })
  );
  return snapshot;
}

export function hydrateCartIndicators(
  root: ParentNode,
  snapshot: CommerceCartSnapshot | null = readCommerceCart()
): void {
  const count = snapshot?.items.reduce((sum, item) => sum + item.qty, 0) ?? 0;
  const indicators = Array.from(root.querySelectorAll<HTMLElement>(CART_COUNT_SELECTOR));
  if (root instanceof HTMLElement && root.matches(CART_COUNT_SELECTOR)) indicators.unshift(root);
  for (const indicator of indicators) {
    indicator.textContent = String(count);
    indicator.dataset.plpCartEmpty = String(count === 0);
  }
}

function addVariantToCart(publicId: string, qty: number, replace: boolean): CommerceCartSnapshot {
  const current = replace ? [] : (readCommerceCart()?.items ?? []);
  const next = [...current];
  const existing = next.find((item) => item.variant_public_id === publicId);
  if (existing) existing.qty = Math.min(999, existing.qty + qty);
  else next.push({ variant_public_id: publicId, qty });
  return writeCommerceCart(next);
}

function actionQuantity(element: HTMLElement): number {
  const qty = Number(element.dataset.plpQuantity ?? '1');
  return Number.isSafeInteger(qty) && qty > 0 && qty <= 999 ? qty : 1;
}

function closeCommerceDialog(dialog: HTMLDialogElement): void {
  if (dialog.open && typeof dialog.close === 'function') dialog.close();
  dialog.remove();
  document.documentElement.classList.remove('plp-commerce-modal-open');
}

export function openCheckout(path: string, presentation: string): void {
  if (presentation !== 'modal' || typeof HTMLDialogElement === 'undefined') {
    window.location.assign(path);
    return;
  }

  document.querySelector<HTMLDialogElement>('.plp-commerce-dialog')?.remove();
  const dialog = document.createElement('dialog');
  dialog.className = 'plp-commerce-dialog';
  dialog.setAttribute('aria-label', 'Оформление заказа');

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'plp-commerce-dialog__close';
  close.setAttribute('aria-label', 'Закрыть');
  close.textContent = '×';

  const iframe = document.createElement('iframe');
  iframe.className = 'plp-commerce-dialog__frame';
  iframe.title = 'Оформление заказа';
  iframe.src = `${path}${path.includes('?') ? '&' : '?'}view=modal`;
  iframe.setAttribute('allow', 'payment');

  close.addEventListener('click', () => closeCommerceDialog(dialog));
  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    closeCommerceDialog(dialog);
  });
  dialog.append(close, iframe);
  document.body.appendChild(dialog);
  document.documentElement.classList.add('plp-commerce-modal-open');
  if (typeof dialog.showModal === 'function') {
    dialog.showModal();
  } else {
    window.location.assign(path);
    dialog.remove();
  }
}

async function handleCommerceClick(event: MouseEvent): Promise<void> {
  const target =
    event.target instanceof Element ? event.target.closest<HTMLElement>(ACTION_SELECTOR) : null;
  if (!target) return;
  event.preventDefault();

  if (isEditMode()) {
    target.dispatchEvent(
      new CustomEvent('plp:commerce-action-blocked', {
        bubbles: true,
        detail: { reason: 'edit-mode' },
      })
    );
    return;
  }

  const action = target.dataset.plpAction;
  if (!action || target.getAttribute('aria-busy') === 'true') return;
  if (action === 'cart-open') {
    // Сохраняем manifest даже для пустой корзины: checkout сможет показать
    // корректное пустое состояние и затем проверить только разрешённые товары.
    if (!readCommerceCart()) writeCommerceCart([]);
    openCheckout('/checkout/cart', target.dataset.plpPresentation ?? 'modal');
    return;
  }

  const publicId =
    action === 'checkout-offer'
      ? target.dataset.plpOfferPublicId
      : target.dataset.plpVariantPublicId;
  if (!publicId) return;
  target.setAttribute('aria-busy', 'true');
  try {
    const data = await loadPageCommerceData();
    const projection =
      action === 'checkout-offer' ? data.offers[publicId] : data.variants[publicId];
    if (!projection?.available) {
      target.setAttribute('aria-disabled', 'true');
      if (target instanceof HTMLButtonElement) target.disabled = true;
      return;
    }
    if (action === 'checkout-offer') {
      const pending = await promptPendingOrderForOffer(publicId);
      if (pending.decision === 'cancel') return;
      if (pending.decision === 'continue' && pending.order) {
        openCheckout(pending.order.path, target.dataset.plpPresentation ?? 'modal');
        return;
      }
      openCheckout(
        `/checkout/offer/${encodeURIComponent(publicId)}`,
        target.dataset.plpPresentation ?? 'modal'
      );
      return;
    }

    addVariantToCart(publicId, actionQuantity(target), action === 'checkout-variant');
    if (action === 'checkout-variant') {
      openCheckout('/checkout/cart', target.dataset.plpPresentation ?? 'modal');
    }
  } finally {
    target.removeAttribute('aria-busy');
  }
}

export function initCommerce(root: ParentNode = document): void {
  const manifest = readCommerceManifest();
  if (!manifest) return;
  hydrateCartIndicators(root);
  void initResumeWidget();

  if (!resumeCartListenerInstalled) {
    document.addEventListener('plp:cart-updated', () => {
      void initResumeWidget();
    });
    resumeCartListenerInstalled = true;
  }

  if (!clickListenerInstalled) {
    document.addEventListener('click', (event) => {
      void handleCommerceClick(event).catch(() => undefined);
    });
    clickListenerInstalled = true;
  }

  if (
    root.querySelector(BINDING_SELECTOR) ||
    root.querySelector(ACTION_SELECTOR) ||
    (root instanceof Element && (root.matches(BINDING_SELECTOR) || root.matches(ACTION_SELECTOR)))
  ) {
    void loadPageCommerceData()
      .then((data) => hydrateCommerceBindings(root, data))
      .catch(() => undefined);
  }
}

export function resetCommerceRuntimeForTests(): void {
  pageDataPromise = null;
  resolvedOrdersPromise = null;
  document.querySelector('.plp-commerce-resume')?.remove();
  sessionStorage.removeItem(RESUME_DISMISSED_SESSION_KEY);
}
